import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Newspaper, Lightning, Flame, ArrowSquareOut, ChartBar, CircleNotch, Eye } from "@phosphor-icons/react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ArticleCard({ article, onTrack }) {
  const isElectrical = article.trade === "electrical";
  return (
    <div
      data-testid={`article-${article.id}`}
      className={`wp-card ${isElectrical ? "border-l-2 border-l-[#002FA7]" : "border-l-2 border-l-[#FF5A00]"} animate-slide-up`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold leading-snug" style={{ fontFamily: "'Chivo', sans-serif" }}>
            {article.title}
          </h3>
          <p className="text-[11px] text-[#404040] mt-1 line-clamp-2 leading-relaxed">{article.summary?.replace(/<[^>]*>/g, '')}</p>
          <div className="flex items-center gap-2 mt-2">
            <span className="font-mono-data text-[10px] text-[#404040]">{article.source}</span>
            {article.published && (
              <span className="font-mono-data text-[10px] text-[#404040]">
                {new Date(article.published).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </span>
            )}
          </div>
        </div>
        <a
          href={article.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => onTrack(article)}
          data-testid={`read-article-${article.id}`}
          className="shrink-0 touch-target w-10 h-10 bg-[#F4F4F5] rounded-sm flex items-center justify-center hover:bg-[#0A0A0A] hover:text-white transition-colors"
        >
          <ArrowSquareOut size={16} />
        </a>
      </div>
    </div>
  );
}

export default function NewsPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);

  const fetchNews = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/news`);
      setArticles(res.data);
    } catch {
      toast.error("Failed to load news");
    }
    setLoading(false);
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/news/analytics`);
      setAnalytics(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchNews(); fetchAnalytics(); }, [fetchNews, fetchAnalytics]);

  const trackClick = async (article) => {
    try {
      await axios.post(`${API}/news/click`, {
        url: article.url, title: article.title,
        source: article.source, trade: article.trade
      });
    } catch {}
  };

  const electrical = articles.filter(a => a.trade === "electrical");
  const hvac = articles.filter(a => a.trade === "hvac" || a.trade === "general");

  return (
    <div className="p-4 max-w-lg mx-auto" data-testid="news-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-black tracking-tight mb-1" style={{ fontFamily: "'Chivo', sans-serif" }}>
            Industry News
          </h1>
          <p className="text-xs text-[#404040] font-mono-data">ELECTRICAL & HVAC</p>
        </div>
        <button
          data-testid="toggle-analytics"
          onClick={() => { setShowAnalytics(!showAnalytics); fetchAnalytics(); }}
          className="p-2 border border-[#E5E5E5] rounded-sm hover:border-[#0A0A0A] transition-colors"
        >
          <ChartBar size={16} className="text-[#404040]" />
        </button>
      </div>

      {/* Analytics Panel */}
      {showAnalytics && analytics && (
        <div className="wp-card mb-4 animate-slide-up" data-testid="news-analytics">
          <h3 className="font-mono-data text-[10px] text-[#002FA7] mb-2 flex items-center gap-1">
            <Eye size={10} /> CLICK ANALYTICS
          </h3>
          <div className="grid grid-cols-2 gap-3 mb-2">
            <div><span className="metric-label">TOTAL CLICKS</span><div className="metric-value">{analytics.total_clicks}</div></div>
            <div><span className="metric-label">SOURCES</span><div className="metric-value">{Object.keys(analytics.by_source).length}</div></div>
          </div>
          {Object.keys(analytics.by_source).length > 0 && (
            <div className="space-y-1">
              {Object.entries(analytics.by_source).sort((a,b) => b[1]-a[1]).map(([src, count]) => (
                <div key={src} className="flex items-center justify-between text-xs">
                  <span className="text-[#404040] truncate">{src}</span>
                  <span className="font-mono-data text-[10px] font-bold">{count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Tabs defaultValue="all">
        <TabsList className="grid w-full grid-cols-3 h-9 mb-4">
          <TabsTrigger value="all" className="text-xs" data-testid="news-all-tab">All</TabsTrigger>
          <TabsTrigger value="electrical" className="text-xs flex items-center gap-1" data-testid="news-electrical-tab">
            <Lightning size={10} /> Electrical
          </TabsTrigger>
          <TabsTrigger value="hvac" className="text-xs flex items-center gap-1" data-testid="news-hvac-tab">
            <Flame size={10} /> HVAC
          </TabsTrigger>
        </TabsList>

        {loading ? (
          <div className="flex justify-center py-12"><CircleNotch size={28} className="animate-spin text-[#002FA7]" /></div>
        ) : (
          <>
            <TabsContent value="all">
              <div className="space-y-3">
                {articles.map((a, i) => <div key={a.id} style={{ animationDelay: `${i * 0.05}s` }}><ArticleCard article={a} onTrack={trackClick} /></div>)}
              </div>
            </TabsContent>
            <TabsContent value="electrical">
              <div className="space-y-3">
                {electrical.map((a, i) => <div key={a.id} style={{ animationDelay: `${i * 0.05}s` }}><ArticleCard article={a} onTrack={trackClick} /></div>)}
              </div>
            </TabsContent>
            <TabsContent value="hvac">
              <div className="space-y-3">
                {hvac.map((a, i) => <div key={a.id} style={{ animationDelay: `${i * 0.05}s` }}><ArticleCard article={a} onTrack={trackClick} /></div>)}
              </div>
            </TabsContent>
          </>
        )}
      </Tabs>
    </div>
  );
}
